import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, Shield, User as UserIcon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_PERMS = [
  // Core ops
  { id: "chat", label: "Chat con clientes", default: true },
  { id: "messages", label: "Mensajes privados / IA" },
  { id: "users", label: "Gestionar usuarios" },
  { id: "appts", label: "Citas" },
  { id: "plans", label: "Pagos / Trabajos" },
  { id: "readings", label: "Lecturas online" },
  // Content
  { id: "horoscope", label: "Horóscopos" },
  { id: "broadcast", label: "Broadcast" },
  // Sorteo & vidente
  { id: "sorteo", label: "Sorteo (incl. live button)" },
  { id: "vidente", label: "Vidente Virtual (reseñas + monitor)" },
  // Stats & dashboards
  { id: "overview", label: "Resumen del panel" },
  { id: "activity", label: "Actividad en vivo" },
  { id: "reports", label: "Reportes / Administración" },
  // Configuration
  { id: "settings", label: "Configuración general" },
  { id: "site_config", label: "Configuración avanzada del sitio" },
  { id: "database", label: "Base de datos (peligroso)" },
];

export const CreateUserModal = ({ open, onClose, onCreated }) => {
  const { authHeader } = useAuth();
  const [form, setForm] = useState({
    username: "", email: "", password: "", name: "",
    role: "user", age: "", country: "", phone: "",
    permissions: ["chat"],
  });
  const [saving, setSaving] = useState(false);

  const togglePerm = (id) => {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(id) ? f.permissions.filter((x) => x !== id) : [...f.permissions, id],
    }));
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.username || !form.email || !form.password || !form.name) return toast.error("Campos requeridos");
    if (form.password.length < 6) return toast.error("Contraseña mínimo 6 caracteres");
    setSaving(true);
    try {
      const payload = {
        username: form.username.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        name: form.name.trim(),
        role: form.role,
        age: form.age ? Number(form.age) : null,
        country: form.country || null,
        phone: form.phone || null,
        permissions: form.role === "admin" ? form.permissions : null,
      };
      await axios.post(`${API}/admin/users`, payload, { headers: authHeader() });
      toast.success(`${form.role === "admin" ? "Admin" : "Usuario"} creado`);
      onCreated?.();
      onClose();
      setForm({ username: "", email: "", password: "", name: "", role: "user", age: "", country: "", phone: "", permissions: ["chat"] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0a0a0a] border border-[#d4af37]/30 text-white max-w-xl max-h-[90vh] overflow-y-auto" data-testid="create-user-modal">
        <DialogHeader>
          <DialogTitle className="font-serif flex items-center gap-2">
            {form.role === "admin" ? <Shield className="w-5 h-5 text-amber-300" /> : <UserIcon className="w-5 h-5 text-emerald-300" />}
            Crear {form.role === "admin" ? "Administrador" : "Usuario"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-300">Tipo de cuenta</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="create-role"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0a0a0a] border-[#d4af37]/30 text-white">
                <SelectItem value="user">👤 Usuario normal</SelectItem>
                <SelectItem value="admin">👑 Administrador</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-neutral-300">Usuario</Label>
              <Input data-testid="create-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="bg-black/40 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-xs text-neutral-300">Contraseña</Label>
              <Input data-testid="create-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-black/40 border-white/10 text-white" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-neutral-300">Email</Label>
              <Input data-testid="create-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-black/40 border-white/10 text-white" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-neutral-300">Nombre completo</Label>
              <Input data-testid="create-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black/40 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-xs text-neutral-300">Edad</Label>
              <Input type="number" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="bg-black/40 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-xs text-neutral-300">País</Label>
              <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="bg-black/40 border-white/10 text-white" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-neutral-300">Teléfono / WhatsApp</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-black/40 border-white/10 text-white" />
            </div>
          </div>

          {form.role === "admin" && (
            <div className="border border-amber-500/30 rounded-lg p-3 bg-amber-500/5">
              <div className="text-xs uppercase tracking-widest text-amber-300 mb-2 flex items-center gap-1.5"><Shield className="w-3 h-3" /> Permisos del administrador</div>
              <p className="text-[10px] text-neutral-400 mb-2">Selecciona qué secciones podrá usar. Los admins SIEMPRE reciben los mensajes de los usuarios.</p>
              <div className="grid grid-cols-2 gap-1.5">
                {ALL_PERMS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`perm-${p.id}`}
                    onClick={() => togglePerm(p.id)}
                    className={`text-xs px-2 py-1 rounded border text-left transition ${form.permissions.includes(p.id) ? "border-amber-500 bg-amber-500/15 text-amber-200" : "border-white/10 text-neutral-400 hover:text-white"}`}
                  >
                    {form.permissions.includes(p.id) ? "✓" : "○"} {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <button data-testid="create-user-submit" type="submit" disabled={saving} className="btn-gold w-full py-3 rounded-full uppercase text-xs tracking-widest flex items-center justify-center gap-2">
            <UserPlus className="w-3.5 h-3.5" />
            {saving ? "Creando..." : `Crear ${form.role === "admin" ? "Administrador" : "Usuario"}`}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
